import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

interface EdgeFunctionOptions {
  body?: any;
  headers?: Record<string, string>;
}

interface EdgeFunctionResponse<T = any> {
  data?: T;
  error?: {
    message: string;
    code?: string;
    details?: any;
  };
}

/**
 * Safely invoke a Supabase Edge Function with proper error handling
 * Handles common issues like:
 * - Edge Functions not running locally
 * - HTML responses instead of JSON
 * - Network errors
 */
export async function invokeEdgeFunction<T = any>(
  functionName: string,
  options: EdgeFunctionOptions = {},
): Promise<EdgeFunctionResponse<T>> {
  try {
    const { data, error } = await supabase.functions.invoke(functionName, {
      body: options.body,
      headers: options.headers,
    });

    // Check if response is HTML (Edge Functions not running)
    if (typeof data === 'string' && data.includes('<!DOCTYPE')) {
      logger.error(
        `Edge Function "${functionName}" returned HTML. Make sure 'supabase functions serve' is running.`,
      );
      return {
        error: {
          message:
            'Edge Functions are not running. Please start them with: supabase functions serve',
          code: 'EDGE_FUNCTIONS_NOT_RUNNING',
          details: 'Received HTML response instead of JSON',
        },
      };
    }

    if (error) {
      return {
        error: {
          message: error.message || 'Edge Function error',
          code: error.code,
          details: error,
        },
      };
    }

    return { data };
  } catch (error: any) {
    // Handle network errors, parsing errors, etc.
    logger.error(`Edge Function "${functionName}" error:`, error);

    return {
      error: {
        message: error.message || 'Failed to invoke Edge Function',
        code: 'NETWORK_ERROR',
        details: error,
      },
    };
  }
}

/**
 * Check if Edge Functions are available
 */
export async function checkEdgeFunctionsStatus(): Promise<boolean> {
  try {
    // Skip health check in development as /health endpoint doesn't exist
    // Edge Functions don't have a standard health endpoint
    if (import.meta.env.DEV) {
      // Assume functions are available if we're in dev mode
      // The actual function calls will fail if they're not running
      return true;
    }

    // In production, try to check if functions are available
    // Add timeout to prevent hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 1000); // 1 second timeout

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1`, {
      method: 'GET',
      headers: {
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // If we get HTML back, functions aren't available
    const text = await response.text();
    if (text.includes('<!DOCTYPE')) {
      return false;
    }

    return response.ok;
  } catch {
    // Silently fail - don't log errors for health checks
    return false;
  }
}
